/**
 * PlateDocumentDrawer — the reusable Plate-based document editor drawer.
 *
 * This is the generic engine behind every Plate "letter/consent/one-pager"
 * surface (Engagement Letter, NDA, Bureau & KYC Consent, …). It owns ALL the
 * shared behaviour — saved-doc fetch + Slate→Plate conversion, the BottomDrawer
 * chrome, PDF export (raster + print), regenerate, the comments side-pane, and
 * save (image processing + persist + Mixpanel) — and delegates only the
 * doc-specific bits to props:
 *
 *   • redux slice values  → orgId / companyName / loading / onClose / setLoading
 *   • API specifics       → buildInitialTemplate() (the doc's own template fetch)
 *   • identity / chrome    → documentType / title / ids / upload sources / save event
 *   • optional transform   → normalizeOnLoad() (e.g. EL strips legacy 16px marks)
 *
 * To add a new Plate document type, write a ~30-line wrapper that reads its
 * redux slice and passes these props — no chrome/export/save code to copy.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import clsx from 'clsx';
import SyncRoundedIcon from '@mui/icons-material/SyncRounded';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { getDocument, saveDocument } from '../../store/investor/investorApiHelper';
import { unwrapDocVersion } from '../PlateEditor/migrations';
import { useCommitDiffGate } from '../hooks/useCommitDiffGate';
import { regenerateIcon, PURPLE_DROPDOWN_CARET, editIconPurple, ClockCounterDark } from '../../assets/hostedassets';
import {
    PlateEditor,
    PreviewButton,
    slateToPlateValue,
    normalizeTableColSizesInValue,
    stampOrderedListStarts,
    isSlateShape,
    processImagesBeforeSave,
    collectDocIdsFromSlate,
    exportEditorToPdfServer,
    exportEditorToDocxFile,
    ProgressBorder,
    ExportingLabel,
    useExportProgress,
} from '../PlateEditor';
import { toast } from 'react-toastify';
import { Button, Modal } from '../_custom';
import { BottomDrawer } from '../BottomDrawer';
import LoadingSpinner from '../_custom/LoadingSpinner/LoadingSpinner';
import HistorySideDrawer, { HistoryEntry } from '../HistorySideDrawer/HistorySideDrawer';
import { Mixpanel } from '../../utils/mixpanel';
import { getSubdomain } from '../../utils/utils';
import { formatDateByFormat, DATE_FORMATS } from '../../utils/dateUtils';
import { useFeatureVisibility } from '../../utils/useVisibilityConfig';
import { VISIBILITY_KEYS } from '../../utils/constants/visibilityKeys';
import { resetAndHideCommentsThread, closeCommentsThread } from '../../store/commentsThread/commentsThread_actions';
import styles from './PlateDocumentDrawer.module.scss';

export type AutosaveStatus = 'idle' | 'saving' | 'saved' | 'error';

/** Reuses the shared HistorySideDrawer shell (same component + UX as OnePager). */
export interface PlateDrawerHistoryProps {
    isOpen: boolean;
    isExpanded: boolean;
    loading: boolean;
    historyEntries: HistoryEntry[];
    onToggle: () => void;
    onClose: () => void;
    onToggleExpand: () => void;
    /** Row click → load that version into the editor in place (preview, no save). */
    onPreviewEntry: (entry: HistoryEntry) => void;
    /** Restore (3-dot menu confirm or footer "Restore this version") → re-save as a new version. */
    onSelectEntry: (entry: HistoryEntry) => void;
    /** Portal target id — must be unique per doc surface, mirrors toolbarHostId/commentContainerId. */
    portalTargetId: string;
    disabled?: boolean;
    /** The currently-selected/previewed entry id — drives row highlight + the footer restore button. */
    selectedEntryId?: string | null;
    /** True while a version is being fetched/restored — disables the footer restore button. */
    previewLoading?: boolean;
    /**
     * Plate nodes of the version to show in the editor right now (in-place preview).
     * A NEW array reference here loads that content and remounts the editor read-only;
     * `null` (the default) leaves the live doc in place. The caller re-sets this to the
     * latest content when the drawer closes on an older preview (reload-latest cleanup).
     */
    previewContent?: any[] | null;
    /** Fired when the footer "Restore this version" button is clicked (before confirm). */
    onRestoreConfirm?: () => void;
}

export type PlateDocumentType = 'ENGAGEMENT_LETTER' | 'NDA' | 'BUREAU_KYC_CONSENT' | 'ONE_PAGER' | 'APPLICATION_FORM' | 'REPORTS';

export interface BuildInitialTemplateArgs {
    orgId: string;
    investorOrgId: string;
    emailId: string;
    companyName: string;
    companyMainImg: string;
    dispatch: ReturnType<typeof useAppDispatch>;
}

export interface PlateDocumentDrawerProps {
    /** Persistence key — selects which saved doc lineage to load and where to save. */
    documentType: PlateDocumentType;
    /** Header title + regenerate-copy noun, e.g. "Engagement Letter". */
    title: string;
    /** Export filename prefix, e.g. "Engagement_Letter". */
    fileNamePrefix: string;
    /** Unique BottomDrawer id (one per doc surface). */
    drawerId: string;
    /** Unique toolbar portal host id (one per doc surface). */
    toolbarHostId: string;
    /** Unique comments portal target id (one per doc surface). */
    commentContainerId: string;
    /** PlateEditor image upload source bucket, e.g. "engagement-letter". */
    uploadSource: string;
    /** processImagesBeforeSave document key, e.g. "engagementLetter". */
    imageDocumentType: string;
    /** Mixpanel event fired on save. */
    mixpanelSaveEvent: string;

    // ── doc-specific redux slice values (supplied by the wrapper) ──
    orgId: string;
    companyName: string;
    loading: boolean;
    onClose: () => void;
    setLoading: (loading: boolean) => void;

    /**
     * Per-doc fallback template builder. Called when no saved doc exists (or on
     * regenerate). Encapsulates the doc's own template-fetch + metadata flow and
     * returns the initialized (Slate- or Plate-shape) value. MUST be referentially
     * stable (wrap in useCallback) — it's in the fetch effect's deps.
     */
    buildInitialTemplate: (args: BuildInitialTemplateArgs) => Promise<any[]>;

    /** Optional load-time transform on the converted Plate value. */
    normalizeOnLoad?: (value: any[]) => any[];

    /**
     * View-to-attach (RECUR-23138): when the drawer was opened from Compose
     * Email, this captures the rendered editor to a PDF and hands the docId
     * back to the modal (as an attachment chip) after Save & Close, before the
     * drawer unmounts. Supplied by the wrapper from its redux slice's
     * `onComposeAttach`; absent in the normal (non-compose) open path.
     */
    onComposeAttach?: (canvasEl: HTMLElement, filename: string) => Promise<void>;

    /**
     * When present, replaces the default lineage load in the primary load path.
     * Returning `null` means no saved doc exists — falls through to
     * `buildInitialTemplate`. Used by ReportEditorRoute to load GeneratedDocuments.
     */
    loadOverride?: () => Promise<any[] | null>;

    /**
     * When present, replaces the default `saveDocument` commit in the save path.
     * Used by ReportEditorRoute to persist GeneratedDocuments.
     */
    persistOverride?: (content: any[]) => Promise<void>;

    /**
     * When present, renders the static title + a purple "Edit" chip (same
     * markup/styling as OnePager's DealSummaryEditorPlate) instead of the plain
     * `title — companyName` string. The chip's click is owned by the caller —
     * ReportEditorRoute opens a name+description rename dialog from it, since
     * Reports (unlike OnePager) persists the name and also has a description.
     */
    editableTitle?: { value: string; onEditClick: () => void };

    /**
     * Fired on every editor change with the live Plate value. Lets a caller
     * drive its own behaviour off content edits (e.g. ReportEditorRoute's
     * debounced DRAFT autosave) without the drawer owning save cadence.
     * Additive + optional — the drawer's internal `editorContentRef` tracking
     * is unchanged.
     */
    onContentChange?: (value: any[]) => void;

    /**
     * When present, renders the autosave sync-status indicator in the header
     * actions row — same icon/label markup as OnePager's headerSyncStatus
     * (rotating sync while saving, cloud-checkmark once saved).
     */
    autosaveStatus?: AutosaveStatus;

    /**
     * When present, renders word count (left) + "Last edit: … by …" (center)
     * in the footer, same layout as OnePager's footer.
     */
    footerMeta?: { wordCount: number; lastEditedAt?: number; lastEditedBy?: string };

    /**
     * Whether the footer Regenerate button is shown. Defaults to true (template-backed
     * docs regenerate from their template). Callers pass false for docs with no template
     * to regenerate from — a blank or imported report — where regenerate would only wipe
     * the content to an empty document.
     */
    canRegenerate?: boolean;

    /**
     * When present, renders a History icon button in the header (same spot/
     * styling as OnePager's) and the sliding history side-pane in the drawer's
     * rightSlot. The drawer owns none of the history DATA — the caller supplies
     * already-fetched entries + callbacks (see useHistoryDrawer), matching how
     * every other doc-specific concern here is delegated to the wrapper.
     */
    history?: PlateDrawerHistoryProps;
}

export function PlateDocumentDrawer({
    documentType,
    title,
    fileNamePrefix,
    drawerId,
    toolbarHostId,
    commentContainerId,
    uploadSource,
    imageDocumentType,
    mixpanelSaveEvent,
    orgId,
    companyName,
    loading,
    onClose,
    setLoading,
    buildInitialTemplate,
    normalizeOnLoad,
    onComposeAttach,
    loadOverride,
    persistOverride,
    editableTitle,
    onContentChange,
    autosaveStatus,
    footerMeta,
    canRegenerate = true,
    history,
}: PlateDocumentDrawerProps) {
    const dispatch = useAppDispatch();
    const ownerOptions = useAppSelector((state) => state.investor.ownerDropdownOptions) || [];
    const commentsThreadState = useAppSelector((state) => state.commentsThreadReducer);
    const commentsActive = Boolean(commentsThreadState?.showCommentsThread && !commentsThreadState?.closingCommentsThread);

    // Gate the AI assistant on the same `editor.AI.enabled`.write flag the
    // floating-toolbar "Ask Aica" button self-gates on. Without this, the button
    // rendered (flag on) but the AIMenu never mounted and the toggle listener
    // never attached (enableAI defaulted false) — clicking it did nothing.
    const { aiEnabled } = useFeatureVisibility({ aiEnabled: VISIBILITY_KEYS.AI.ENABLED });

    const investorOrgId = useAppSelector((state) => state.user.userData.investorOrgId);
    const emailId = useAppSelector((state) => state.user.userData.primaryEmail);
    const user = useAppSelector((state) => state.user.userData);
    const companyMainImg = useAppSelector((state) => state.user.domainMetadata?.mainImageUrl);

    const [editorInitialData, setEditorInitialData] = useState<any>(null);
    const [editorReady, setEditorReady] = useState(false);
    // Set when the saved-doc load (loadOverride/getDocument) REJECTS
    // — as opposed to succeeding with an empty result, which legitimately means
    // "no doc yet" and falls through to buildInitialTemplate. On a load failure
    // we must NOT fall through: doing so would mount a fresh template as
    // initialValue, and Save & Close would silently overwrite the existing doc
    // with it after a transient failure (RECUR-26249).
    const [loadError, setLoadError] = useState(false);
    const [saving, setSaving] = useState(false);
    const [showRegenConfirmModal, setShowRegenConfirmModal] = useState(false);
    const exportProgress = useExportProgress();
    // Derived so the existing `disabled={… || exporting}` guards keep working.
    const exporting = exportProgress.isRunning;
    // Bumped on regenerate to force-remount the PlateEditor so it picks up the
    // freshly-fetched template as its `initialValue` (the editor only reads
    // initialValue on mount).
    const [editorKey, setEditorKey] = useState(0);

    const editorContentRef = useRef<any[]>([]);
    const originalDocIdsRef = useRef<string[]>([]);
    const canvasRef = useRef<HTMLDivElement>(null);
    // Lineage id of the loaded/saved doc. Latched from the loaded entry (or from
    // the row the backend mints on the first save) and forwarded on every save so
    // versions group under one lineage. Regenerate never re-mints.
    const genIdRef = useRef<string | undefined>(undefined);
    // Commit-diff gate: baseline captured on the first onChange after a load
    // (post Slate→Plate conversion + normalize, so it compares apples to apples
    // with later editor values). Save & Close commits a new version only when the
    // content differs from the baseline; unlatched (fresh template / regenerate /
    // no saved doc) always commits.
    const commitGate = useCommitDiffGate();
    const captureBaselineRef = useRef(false);

    // In-place version preview (mirrors DealSummaryEditorPlate.handleLoadVersion):
    // when the caller hands us a historical version's Plate nodes, swap them into
    // the editor and remount it (readOnly while history is open) instead of the
    // legacy full-page navigate. Reference-compared so re-setting the SAME array
    // (or the initial null) is a no-op — the caller always passes a fresh array
    // per version fetch (getDocument), including the reload-latest-on-close path.
    const lastPreviewRef = useRef<any[] | null>(null);
    useEffect(() => {
        const preview = history?.previewContent;
        if (!preview || preview === lastPreviewRef.current) return;
        lastPreviewRef.current = preview;
        setEditorInitialData(preview);
        originalDocIdsRef.current = collectDocIdsFromSlate(preview);
        editorContentRef.current = preview;
        setEditorKey((k) => k + 1);
    }, [history?.previewContent]);

    // Footer "Restore this version" confirm — same two-step (button → confirm)
    // flow as DealSummaryEditorPlate, kept in the shared drawer so every history-
    // enabled Plate doc gets it for free (mirrors the regenerate-confirm modal).
    const [showRestoreModal, setShowRestoreModal] = useState(false);

    // Fallback: build a fresh template via the doc's own API flow when no saved
    // document exists. `isRegenerate=true` skips the editorReady reset so the
    // drawer chrome doesn't blink on regenerate. Regenerated/fresh content has no
    // committed baseline — Save & Close after it always commits.
    const fetchDataForDoc = useCallback(
        async (isRegenerate = false) => {
            if (!isRegenerate) setEditorReady(false);
            setLoadError(false);
            setLoading(true);
            commitGate.reset();
            captureBaselineRef.current = false;
            try {
                const initialized = await buildInitialTemplate({
                    orgId,
                    investorOrgId: investorOrgId as string,
                    emailId: emailId as string,
                    companyName,
                    companyMainImg: companyMainImg as string,
                    dispatch,
                });
                setEditorInitialData(initialized);
                originalDocIdsRef.current = collectDocIdsFromSlate(initialized);
                editorContentRef.current = initialized;
                setEditorKey((k) => k + 1);
                setEditorReady(true);
            } catch (err) {
                // Composer failed — do not fall through to a blank editable doc, which
                // would be committable via Save & Close as if generation had succeeded.
                console.error('[PlateDocumentDrawer] buildInitialTemplate failed', err);
                toast.error(`Could not generate the ${title.toLowerCase()}. Please try again.`);
                setLoadError(true);
                setEditorReady(true);
            } finally {
                setLoading(false);
            }
        },
        [orgId, investorOrgId, emailId, companyMainImg, companyName, title, dispatch, buildInitialTemplate, setLoading],
    );

    // Primary load: fetch the org's newest live doc of this documentType
    // (autosave draft preferred — even a never-committed one — else the latest
    // committed version). Falls back to fetchDataForDoc when none exists.
    const fetchTemplateData = useCallback(() => {
        setEditorReady(false);
        setLoadError(false);
        setLoading(true);
        // A re-load (e.g. orgId change) must not carry the previous doc's commit
        // baseline into the next one — a stale baseline could wrongly skip a
        // legitimate Save & Close on the newly-loaded doc.
        commitGate.reset();
        captureBaselineRef.current = false;
        if (loadOverride) {
            loadOverride()
                .then((nodes) => {
                    if (nodes?.length) {
                        setEditorInitialData(nodes);
                        originalDocIdsRef.current = collectDocIdsFromSlate(nodes);
                        setEditorReady(true);
                        setLoading(false);
                    } else {
                        // Load succeeded with no saved doc — genuinely a new doc, so
                        // building a fresh template is correct here.
                        fetchDataForDoc();
                    }
                })
                .catch((err) => {
                    // Load REJECTED — do not fall through to a fresh template, which
                    // would be committable via Save & Close over the existing doc.
                    console.error('[PlateDocumentDrawer] loadOverride failed', err);
                    setLoadError(true);
                    setEditorReady(true);
                    setLoading(false);
                });
            return;
        }
        genIdRef.current = undefined;
        getDocument({ assesseeOrgId: orgId, documentType })
            .then(async (doc) => {
                const { value } = unwrapDocVersion(doc?.content ?? []);
                if (value?.length) {
                    genIdRef.current = doc?.genId;
                    // Snapshot the baseline off the FIRST editor onChange (post
                    // conversion/normalize) — only a committed row is a baseline; a
                    // loaded DRAFT means uncommitted changes that Save must commit.
                    captureBaselineRef.current = doc?.status === 'READY';
                    setEditorInitialData(value);
                    originalDocIdsRef.current = collectDocIdsFromSlate(value);
                    setEditorReady(true);
                    setLoading(false);
                } else {
                    fetchDataForDoc();
                }
            })
            .catch((err) => {
                console.error('[PlateDocumentDrawer] saved-document load failed', err);
                setLoadError(true);
                setEditorReady(true);
                setLoading(false);
            });
    }, [orgId, documentType, fetchDataForDoc, setLoading, loadOverride]);

    useEffect(() => {
        dispatch(resetAndHideCommentsThread());
        fetchTemplateData();
        return () => {
            onClose();
        };
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // Re-fetch when orgId changes after mount (e.g. drawer reused for a different
    // org without unmounting). Skip the initial render — the mount effect above
    // already covers it, and running both in the same render causes two concurrent
    // fetches where the last response wins.
    const orgIdEffectFirstRun = useRef(true);
    useEffect(() => {
        if (orgIdEffectFirstRun.current) {
            orgIdEffectFirstRun.current = false;
            return;
        }
        if (orgId) fetchTemplateData();
    }, [orgId]); // eslint-disable-line react-hooks/exhaustive-deps

    // Convert Slate-shape legacy docs to Plate once at load; already-Plate docs
    // pass through. Apply the optional per-doc normalize transform.
    const initialValue = useMemo(() => {
        if (!Array.isArray(editorInitialData) || editorInitialData.length === 0) return undefined;
        const plate = isSlateShape(editorInitialData) ? slateToPlateValue(editorInitialData) : editorInitialData;
        // Repair mis-scaled legacy table colSizes (percentages stored as px) before the
        // editor mounts, so column resize is stable for every loaded doc — Slate-origin
        // (converted above) and already-Plate autosaved docs alike (RECUR-26249).
        const scaled = normalizeTableColSizesInValue(plate);
        // Stamp @platejs/list's listStart convention (ordinal on items 2+,
        // none on a run's first item) so docs saved without it — anything
        // composed before polus emitted listStart — number correctly when the
        // user hits Enter mid-list (see stampOrderedListStarts).
        const numbered = stampOrderedListStarts(scaled);
        return (normalizeOnLoad ? normalizeOnLoad(numbered) : numbered) as any;
    }, [editorInitialData, normalizeOnLoad]);

    // Read via a ref (not the closure) so the memoized onChange handler always sees
    // the current preview state without being re-created on every history toggle.
    const historyOpenRef = useRef(false);
    useEffect(() => {
        historyOpenRef.current = !!history?.isOpen;
    }, [history?.isOpen]);

    const handlePlateChange = useCallback(
        (plateValue: any) => {
            editorContentRef.current = plateValue;
            // First onChange after a committed-doc load is Plate's mount flush with
            // the converted initial value — snapshot it as the Save & Close baseline.
            if (captureBaselineRef.current) {
                captureBaselineRef.current = false;
                commitGate.latch(plateValue);
            }
            // A version-history preview swaps the editor to an old version and remounts
            // it (readOnly), which fires a mount onChange. Do NOT propagate that as a
            // user edit — otherwise merely previewing an old version autosaves it in
            // place over the live document (RECUR-26249). Restore persists explicitly
            // via persistOverride, so it is unaffected by this gate.
            if (historyOpenRef.current) return;
            onContentChange?.(plateValue);
        },
        [onContentChange],
    );

    const safeExportName = useCallback(() => {
        // Prefer the current editable title (e.g. a Reports doc the user has
        // renamed) — falls back to the generic fileNamePrefix + companyName
        // default when there's no editable title or it's been cleared to empty.
        const base = editableTitle?.value?.trim() ? editableTitle.value : `${fileNamePrefix}${companyName ? `_${companyName}` : ''}`;
        return base.replace(/[\\/:*?"<>|]+/g, '-').replace(/\s+/g, '_');
    }, [fileNamePrefix, companyName, editableTitle]);

    const handleSaveAndClose = useCallback(async () => {
        if (saving) return;
        // Previewing a historical version: editorContentRef currently holds that
        // OLD content (the preview effect above swaps it in), so Save & Close must
        // not silently commit it over the live doc without the restore confirm.
        // Restoring goes through the explicit "Restore this version" flow instead.
        if (history?.isOpen) return;
        // The saved doc failed to load — there is nothing safe to save (no fresh
        // template was mounted either). Just close; don't risk writing over the
        // existing doc.
        if (loadError) {
            onClose();
            return;
        }
        const content = editorContentRef.current?.length
            ? editorContentRef.current
            : Array.isArray(editorInitialData)
            ? editorInitialData
            : [];
        if (!content.length) {
            dispatch(closeCommentsThread());
            onClose();
            return;
        }
        // No diff since the loaded committed version → nothing to commit. Skip the
        // save (no version churn) but still run the close/attach flow.
        if (!persistOverride && commitGate.isUnchanged(content)) {
            if (onComposeAttach && canvasRef.current) {
                setSaving(true);
                try {
                    await onComposeAttach(canvasRef.current, safeExportName());
                } finally {
                    setSaving(false);
                }
            }
            dispatch(closeCommentsThread());
            onClose();
            return;
        }
        setSaving(true);
        try {
            const processed = await processImagesBeforeSave(content, originalDocIdsRef.current, imageDocumentType);
            originalDocIdsRef.current = collectDocIdsFromSlate(processed);
            if (persistOverride) {
                await persistOverride(processed);
            } else {
                // Commit a new version under the lineage (the backend promotes the
                // live draft when one exists, else mints a fresh committed row). The
                // first-ever save has no genId — the backend mints one; latch it.
                const saved = await saveDocument({
                    assesseeOrgId: orgId,
                    investorOrgId: investorOrgId as string,
                    createdBy: emailId as string,
                    ...(genIdRef.current ? { genId: genIdRef.current } : {}),
                    documentType,
                    name: title,
                    editor: 'plate',
                    formatType: 'DOC',
                    status: 'READY',
                    content: { version: 1, editor: 'plate', value: processed },
                });
                if (saved?.genId && !genIdRef.current) genIdRef.current = saved.genId;
                commitGate.latch(content);
            }
            Mixpanel.track(mixpanelSaveEvent, {
                userRole: user?.accountType,
                pnoPlatform: getSubdomain(),
                userId: user?.userId,
                currentUrl: window.location.href,
                organizationId: orgId,
            });
            // View-to-attach (RECUR-23138): opened from Compose Email — capture
            // the rendered editor to a PDF and hand the docId back to the modal
            // (attachment chip) before the drawer unmounts.
            if (onComposeAttach && canvasRef.current) {
                await onComposeAttach(canvasRef.current, safeExportName());
            }
            dispatch(closeCommentsThread());
            onClose();
        } catch {
            toast.error('Save failed. Please try again.');
        } finally {
            setSaving(false);
        }
    }, [
        saving,
        history?.isOpen,
        loadError,
        editorInitialData,
        orgId,
        investorOrgId,
        emailId,
        user,
        dispatch,
        documentType,
        title,
        imageDocumentType,
        mixpanelSaveEvent,
        onClose,
        onComposeAttach,
        persistOverride,
        safeExportName,
    ]);

    const isLoading = !!loading || !editorReady;
    const headerTitle = editableTitle ? undefined : `${title}${companyName ? ` — ${companyName}` : ''}`;

    const [exportMenuOpen, setExportMenuOpen] = useState(false);
    const exportMenuRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!exportMenuOpen) return;
        const onDocClick = (e: MouseEvent) => {
            if (exportMenuRef.current && !exportMenuRef.current.contains(e.target as Node)) setExportMenuOpen(false);
        };
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') setExportMenuOpen(false);
        };
        document.addEventListener('mousedown', onDocClick);
        document.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('mousedown', onDocClick);
            document.removeEventListener('keydown', onKey);
        };
    }, [exportMenuOpen]);

    const handleExportServer = async () => {
        if (!canvasRef.current || exporting) return;
        try {
            await exportProgress.track(() => exportEditorToPdfServer({ target: canvasRef.current!, filename: safeExportName() }));
        } catch {
            toast.error('PDF export failed. Please try again.');
        }
    };
    // DOCX — serializes the Plate value tree to a real Word document (reads the
    // JSON, not the DOM), so it needs no canvas target.
    const handleExportDocx = async () => {
        if (exporting) return;
        // PDF exports read the live DOM (canvasRef), which always reflects the
        // loaded doc. DOCX reads the JSON value, but editorContentRef only fills
        // once the user edits — so fall back to the displayed value (Plate-
        // converted) for an unedited doc, matching handleSaveAndClose.
        const current = editorContentRef.current?.length ? editorContentRef.current : initialValue ?? [];
        const value = isSlateShape(current) ? slateToPlateValue(current) : current;
        try {
            await exportProgress.track(() => exportEditorToDocxFile({ value, filename: safeExportName() }));
        } catch {
            toast.error('Word export failed. Please try again.');
        }
    };

    const headerActions = (
        <>
            {autosaveStatus && (
                <div className={styles.headerSyncStatus} aria-live="polite">
                    {autosaveStatus === 'saving' ? (
                        <>
                            <SyncRoundedIcon className={clsx(styles.syncIcon, styles.syncIconSpin)} fontSize="small" />
                            <span>Saving</span>
                        </>
                    ) : (
                        <>
                            <svg viewBox="-1 -1 26 26" className={styles.syncIcon} aria-hidden fill="none">
                                <path
                                    stroke="currentColor"
                                    strokeWidth="1.5"
                                    d="M19.35 10.04C18.67 6.59 15.64 4 12 4 9.11 4 6.6 5.64 5.35 8.04 2.34 8.36 0 10.91 0 14c0 3.31 2.69 6 6 6h13c2.76 0 5-2.24 5-5 0-2.64-2.05-4.78-4.65-4.96z"
                                />
                                <path fill="currentColor" d="M10 14.18L7.91 12.09L6.5 13.5L10 17L16.01 10.99L14.60 9.58Z" />
                            </svg>
                            <span>{autosaveStatus === 'error' ? 'Save failed' : 'Saved'}</span>
                        </>
                    )}
                </div>
            )}
            {history && (
                <button
                    type="button"
                    className={clsx(styles.historyBtn, history.isOpen && styles.historyBtnActive)}
                    aria-label="View history"
                    title="Version history"
                    onClick={history.onToggle}
                    disabled={history.disabled}
                >
                    <img src={ClockCounterDark} alt="" aria-hidden className={styles.btnIcon} />
                </button>
            )}
            <div className={styles.exportGroup} ref={exportMenuRef}>
                <button
                    type="button"
                    className={clsx(styles.exportBtn, exportProgress.isRunning && styles.exportBtnExporting)}
                    onClick={handleExportServer}
                    disabled={isLoading || exporting}
                    aria-label="Export as PDF"
                    title={`Export the ${title.toLowerCase()} as a paginated PDF`}
                >
                    {exportProgress.isRunning ? <ExportingLabel percent={exportProgress.percent} /> : <span>Export .pdf</span>}
                </button>
                <button
                    type="button"
                    className={styles.exportCaretBtn}
                    onClick={() => setExportMenuOpen((o) => !o)}
                    disabled={isLoading || exporting}
                    aria-label="Export options"
                    aria-haspopup="true"
                    aria-expanded={exportMenuOpen}
                    title="Other export formats"
                >
                    <img src={PURPLE_DROPDOWN_CARET} className={styles.exportCaret} alt="" aria-hidden />
                </button>
                {exportMenuOpen && (
                    <div className={styles.exportPopover} role="menu" aria-label="Export options">
                        <button
                            type="button"
                            role="menuitem"
                            className={styles.exportPopoverItem}
                            onClick={() => {
                                setExportMenuOpen(false);
                                handleExportServer();
                            }}
                            disabled={isLoading || exporting}
                        >
                            Export as .pdf
                        </button>
                        <button
                            type="button"
                            role="menuitem"
                            className={styles.exportPopoverItem}
                            onClick={() => {
                                setExportMenuOpen(false);
                                handleExportDocx();
                            }}
                            disabled={isLoading || exporting}
                            title="Export as an editable Word (.docx) document"
                        >
                            Export as .docx
                        </button>
                    </div>
                )}
                {exportProgress.isRunning && <ProgressBorder percent={exportProgress.percent} />}
            </div>
            <Button
                text="Save & Close"
                variant="primary"
                onClick={handleSaveAndClose}
                className={styles.headerSaveBtn}
                loading={saving}
                disabled={saving || !!history?.isOpen}
            />
        </>
    );

    // Pinned bar at the bottom of the history pane, shown only while PREVIEWING a
    // non-latest version (index 0 is the current version — nothing to restore).
    // Mirrors DealSummaryEditorPlate's `historyFooter`.
    const selectedHistoryIndex =
        history?.isOpen && history.selectedEntryId ? history.historyEntries.findIndex((e) => e._id === history.selectedEntryId) : -1;
    const showRestoreFooter = selectedHistoryIndex > 0;

    const handleConfirmRestore = useCallback(() => {
        if (!history) return;
        const selectedEntry = history.historyEntries.find((e) => e._id === history.selectedEntryId);
        setShowRestoreModal(false);
        if (!selectedEntry) {
            toast.error('Could not restore — history entry no longer available. Please reopen history and try again.');
            return;
        }
        history.onRestoreConfirm?.();
        history.onSelectEntry(selectedEntry);
    }, [history]);

    return (
        <>
            <BottomDrawer
                open
                onClose={onClose}
                title={
                    editableTitle ? (
                        <div className={styles.headerLeft}>
                            <div className={styles.titleBlock}>
                                <span className={styles.titleText} title={editableTitle.value}>
                                    {editableTitle.value}
                                </span>
                                <div
                                    className={styles.editPill}
                                    onClick={editableTitle.onEditClick}
                                    role="button"
                                    tabIndex={0}
                                    aria-label="Edit name and description"
                                    title="Edit"
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' || e.key === ' ') {
                                            e.preventDefault();
                                            editableTitle.onEditClick();
                                        }
                                    }}
                                >
                                    <img src={editIconPurple} alt="" aria-hidden className={styles.editPillIcon} />
                                    <span>Edit</span>
                                </div>
                            </div>
                        </div>
                    ) : (
                        headerTitle
                    )
                }
                id={drawerId}
                headerRight={headerActions}
                height="100vh"
                className={styles.drawer}
                bodyClassName={styles.body}
                rightSlot={
                    history && (
                        <aside className={clsx(styles.historyPane, history.isOpen && styles.historyPaneOpen)} aria-hidden={!history.isOpen}>
                            <div id={history.portalTargetId} className={styles.historyPortalTarget} />
                            {showRestoreFooter && (
                                <div className={styles.historyPaneFooter}>
                                    <Button
                                        text="Restore this version"
                                        variant="primary"
                                        onClick={() => setShowRestoreModal(true)}
                                        style={{ marginTop: 0, width: '100%' }}
                                        loading={history.previewLoading}
                                    />
                                </div>
                            )}
                        </aside>
                    )
                }
            >
                <div className={styles.toolbarHost} id={toolbarHostId} />
                <div className={styles.editorRow}>
                    <div className={styles.canvas} ref={canvasRef}>
                        {isLoading ? (
                            <div className={styles.loadingPane}>
                                <LoadingSpinner />
                            </div>
                        ) : loadError ? (
                            <div className={styles.errorPane} role="alert">
                                <span>Could not load the {title.toLowerCase()}. Please try again.</span>
                                <Button text="Retry" variant="purpleinverted" onClick={() => fetchTemplateData()} />
                            </div>
                        ) : (
                            <PlateEditor
                                key={editorKey}
                                initialValue={initialValue}
                                className={styles.editor}
                                onChange={handlePlateChange}
                                readOnly={history?.isOpen}
                                fullHeight
                                toolbarHostId={toolbarHostId}
                                uploadSource={uploadSource}
                                mentionableUsers={ownerOptions}
                                enableAI={Boolean(aiEnabled.write)}
                                aiOrgId={orgId}
                            />
                        )}
                    </div>
                    <aside className={clsx(styles.commentsPane, commentsActive && styles.commentsPaneOpen)} aria-hidden={!commentsActive}>
                        <div id={commentContainerId} className={styles.commentsPortalTarget} />
                    </aside>
                </div>
                <div className={clsx(styles.footer, footerMeta && styles.footerSpread)}>
                    {footerMeta && (
                        <>
                            <div className={styles.footerLeft}>
                                <span className={styles.wordCount}>
                                    {footerMeta.wordCount.toLocaleString()} word{footerMeta.wordCount === 1 ? '' : 's'}
                                </span>
                            </div>
                            <div className={styles.footerCenter}>
                                {footerMeta.lastEditedAt && (
                                    <span className={styles.lastEdit}>
                                        Last edit:{' '}
                                        {formatDateByFormat(footerMeta.lastEditedAt, DATE_FORMATS.TIME_DATE_FORMAT_HH_MM_A_DD_MMM_YYYY)}
                                        {footerMeta.lastEditedBy ? ` by ${footerMeta.lastEditedBy}` : ''}
                                    </span>
                                )}
                            </div>
                        </>
                    )}
                    <div className={styles.footerRight}>
                        {canRegenerate && (
                            <button
                                type="button"
                                className={styles.regenerateBtn}
                                onClick={() => setShowRegenConfirmModal(true)}
                                disabled={isLoading}
                                aria-label={`Regenerate ${title.toLowerCase()}`}
                                title={`Regenerate ${title.toLowerCase()}`}
                            >
                                <img src={regenerateIcon} alt="" aria-hidden height="24" />
                                <span>Regenerate</span>
                            </button>
                        )}
                        <PreviewButton
                            getTarget={() => canvasRef.current}
                            filename={safeExportName()}
                            disabled={isLoading}
                            className={styles.footerPreviewBtn}
                        />
                    </div>
                </div>
            </BottomDrawer>

            {history && (
                <HistorySideDrawer
                    containerId={history.portalTargetId}
                    isOpen={history.isOpen}
                    isExpanded={history.isExpanded}
                    title="Version history"
                    loading={history.loading}
                    historyEntries={history.historyEntries}
                    onClose={history.onClose}
                    onToggleExpand={history.onToggleExpand}
                    onSelectEntry={history.onSelectEntry}
                    onPreviewEntry={history.onPreviewEntry}
                    selectedEntryId={history.selectedEntryId}
                    previewLoading={history.previewLoading}
                    onRestoreConfirm={history.onRestoreConfirm}
                />
            )}

            {history && showRestoreModal && (
                <Modal
                    open={showRestoreModal}
                    onClose={() => setShowRestoreModal(false)}
                    size="flexible"
                    className={styles.regenModalOverDrawer}
                >
                    <div className={styles.confirmModal}>
                        <div className={styles.confirmTitle}>Restore this version?</div>
                        <div className={styles.confirmSubtext}>
                            Are you sure you want to restore this version? It will be saved as the newest version of the{' '}
                            {title.toLowerCase()}.
                        </div>
                        <div className={styles.confirmButtons}>
                            <Button
                                text="Cancel"
                                variant="purpleinverted"
                                onClick={() => setShowRestoreModal(false)}
                                style={{ marginTop: '0' }}
                            />
                            <Button text="Restore" variant="primary" onClick={handleConfirmRestore} style={{ marginTop: '0' }} />
                        </div>
                    </div>
                </Modal>
            )}

            {showRegenConfirmModal && (
                <Modal
                    open={showRegenConfirmModal}
                    onClose={() => setShowRegenConfirmModal(false)}
                    size="flexible"
                    className={styles.regenModalOverDrawer}
                >
                    <div className={styles.confirmModal}>
                        <div className={styles.confirmTitle}>Regenerate {title}</div>
                        <div className={styles.confirmSubtext}>
                            Are you sure you want to regenerate the {title}? You will lose all the changes made so far.
                        </div>
                        <div className={styles.confirmButtons}>
                            <Button
                                text="Cancel"
                                variant="purpleinverted"
                                onClick={() => setShowRegenConfirmModal(false)}
                                style={{ marginTop: '0' }}
                            />
                            <Button
                                text="Proceed Anyway"
                                variant="primary"
                                onClick={() => {
                                    setShowRegenConfirmModal(false);
                                    fetchDataForDoc(true);
                                }}
                                style={{ marginTop: '0' }}
                            />
                        </div>
                    </div>
                </Modal>
            )}
        </>
    );
}

export default PlateDocumentDrawer;
